import {useMutation, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'

export type EnfermedadLaboral = components['schemas']['EnfermedadLaboralRead']
export type EnfermedadLaboralCreate = components['schemas']['EnfermedadLaboralCreate']
export type EnfermedadLaboralPatch = components['schemas']['EnfermedadLaboralPatch']
export type EnfermedadLaboralEstado = components['schemas']['EnfermedadLaboralEstado']

const EL_PAGE_SIZE = 10

export function useEmpleadoEnfermedadesLaborales(
  businessId: string | undefined,
  employeeId: string | undefined,
  page = 1
) {
  return usePagedList<EnfermedadLaboral>(
    ['empleado-enfermedades-laborales', businessId, employeeId],
    businessId && employeeId ? `/businesses/${businessId}/employees/${employeeId}/enfermedades-laborales` : undefined,
    {page, search: ''},
    EL_PAGE_SIZE
  )
}

function useElInvalidation(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['empleado-enfermedades-laborales', businessId, employeeId]})
    queryClient.invalidateQueries({queryKey: ['indicadores', businessId]})
  }
}

export function useRegistrarEnfermedadLaboral(businessId: string, employeeId: string) {
  const invalidate = useElInvalidation(businessId, employeeId)
  return useMutation({
    mutationFn: async (payload: EnfermedadLaboralCreate) =>
      (await api.post<EnfermedadLaboral>(`/businesses/${businessId}/enfermedades-laborales`, payload)).data,
    onSuccess: invalidate,
  })
}

export function usePatchEnfermedadLaboral(businessId: string, employeeId: string) {
  const invalidate = useElInvalidation(businessId, employeeId)
  return useMutation({
    mutationFn: async (input: {elId: string; patch: EnfermedadLaboralPatch}) =>
      (
        await api.patch<EnfermedadLaboral>(
          `/businesses/${businessId}/enfermedades-laborales/${input.elId}`,
          input.patch
        )
      ).data,
    onSuccess: invalidate,
  })
}

export function useEliminarEnfermedadLaboral(businessId: string, employeeId: string) {
  const invalidate = useElInvalidation(businessId, employeeId)
  return useMutation({
    mutationFn: async (elId: string) => api.delete(`/businesses/${businessId}/enfermedades-laborales/${elId}`),
    onSuccess: invalidate,
  })
}

export function furelDocumentoUrl(businessId: string, elId: string): string {
  return `/businesses/${businessId}/enfermedades-laborales/${elId}/furel/documento`
}
